/**
 * Temps réel, avec repli.
 *
 * La section 10 impose une chose précise : si la connexion permanente n'est pas
 * disponible, on interroge le serveur toutes les dix secondes. Les écrans
 * n'ont pas à savoir lequel des deux fonctionne — ils s'abonnent, et ce module
 * les prévient qu'il faut se rafraîchir, quelle qu'en soit la source.
 */

import { io, type Socket } from 'socket.io-client';

import { getAccessToken } from './api';

export type FitgoEventType =
  | 'visit:created'
  | 'visit:updated'
  | 'visit:state'
  | 'visit:room'
  | 'visit:caregivers'
  | 'visit:discharged'
  | 'rooms:changed'
  | 'presence:changed'
  | 'config:changed';

export interface FitgoEvent {
  type: FitgoEventType;
  visitId?: string;
  [key: string]: unknown;
}

type Handler = (event: FitgoEvent) => void;

const handlers = new Set<Handler>();
let socket: Socket | null = null;
let pollTimer: number | null = null;

/** Vrai quand la connexion permanente tient ; faux quand on interroge en boucle. */
export const connectionState = {
  live: false,
};

const POLL_INTERVAL_MS = 10_000;

function startPolling(): void {
  if (pollTimer !== null) return;
  connectionState.live = false;
  pollTimer = window.setInterval(() => {
    // Aucun détail à donner : les écrans rechargent ce dont ils ont besoin.
    for (const handler of handlers) handler({ type: 'visit:updated' });
  }, POLL_INTERVAL_MS);
}

function stopPolling(): void {
  if (pollTimer === null) return;
  window.clearInterval(pollTimer);
  pollTimer = null;
}

export function connectRealtime(): void {
  const token = getAccessToken();
  if (token === null) return;

  disconnectRealtime();

  socket = io({
    path: '/api/socket.io',
    auth: { token },
    withCredentials: true,
    // Une seule tentative franche : au-delà, le repli fait le travail sans
    // laisser l'écran vide, ce qui compte plus que la façon d'être à jour.
    reconnectionAttempts: 5,
    reconnectionDelay: 2000,
    timeout: 8000,
  });

  socket.on('connect', () => {
    connectionState.live = true;
    stopPolling();
  });

  socket.on('fitgo', (event: FitgoEvent) => {
    for (const handler of handlers) handler(event);
  });

  socket.on('disconnect', startPolling);
  socket.on('connect_error', startPolling);
}

export function disconnectRealtime(): void {
  socket?.removeAllListeners();
  socket?.disconnect();
  socket = null;
  stopPolling();
  connectionState.live = false;
}

export function onFitgoEvent(handler: Handler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}
